import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from 'carbon-react/lib/components/icon';

import { ExtendedReportTemplateList } from '@futrli/views/processors/reports/reportTemplatesList';
import { FormattedMessage } from '@futrli/localisation';
import { getReportingEditTemplateUrl, matchConsolidationDetailRoutePath } from '@futrli/shared-services';
import { ListingPageRowActionItem } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-actions/listing-page-row-actions';
import { useSidebarContext } from '@futrli/shared-services/contexts/sidebar-context/useSidebarContext';

interface UseReportTemplateActionItemsParams {
  template: ExtendedReportTemplateList;
  onDelete?: () => void;
  onPublish?: () => void;
}

export const useReportTemplateActionItems = ({ template, onDelete, onPublish }: UseReportTemplateActionItemsParams) => {
  const navigate = useNavigate();
  const { workspaceId = '', organisationId = '' } = useParams();
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);
  const { duplicateTemplate, shareResource } = useSidebarContext();

  const items = useMemo<ListingPageRowActionItem[]>(() => {
    const result: ListingPageRowActionItem[] = [];
    const { actions } = template;

    if (actions.edit) {
      result.push({
        label: <FormattedMessage id="common.edit" defaultMessage="Edit" />,
        onClick: () => navigate(getReportingEditTemplateUrl(workspaceId, organisationId, template.id, isConsolidation))
      });
    }

    if (actions.duplicate) {
      result.push({
        label: <FormattedMessage id="common.duplicate" defaultMessage="Duplicate" />,
        onClick: () => {
          duplicateTemplate.open(template);
        }
      });
    }

    if (actions.publish) {
      result.push({
        label: <FormattedMessage id="common.publish" defaultMessage="Publish" />,
        onClick: () => {
          onPublish?.();
        }
      });
    }

    if (actions.share) {
      result.push({
        label: <FormattedMessage id="common.manage_access" defaultMessage="Manage access" />,
        onClick: () => {
          shareResource.open({
            entityId: template.id,
            entityType: 'report-template',
            resourceName: template.name
          });
        }
      });
    }

    if (actions.unpublish) {
      result.push({
        label: <FormattedMessage id="common.unpublish" defaultMessage="Unpublish" />,
        onClick: () => {
          onPublish?.();
        }
      });
    }

    if (actions.delete) {
      result.push({
        label: (
          <>
            <Icon type="delete" fontSize="small" />
            <FormattedMessage id="common.delete" defaultMessage="Delete" />
          </>
        ),
        onClick: () => {
          onDelete?.();
        },
        variant: 'danger'
      });
    }

    return result;
  }, [navigate, workspaceId, organisationId, template, isConsolidation, duplicateTemplate, shareResource, onDelete, onPublish]);

  return items;
};
